import React, { useEffect, useState, useRef } from "react";
import { Modal, Button, Form, Spinner, Badge } from "react-bootstrap";
import toast from "react-hot-toast";
import { Song } from "../../view/spotipitan/types";
import { 
    API_POST_CREATE_OR_GET_FOLDER, 
    API_POST_SONG, 
    CustomApi, 
    clearSongBlobsCache, 
    deleteSong, 
    getCanvasVideoUrl, 
    normalizeFileUrl, 
    updateSong, 
    uploadSongCanvas 
} from "../../../utils/api";
import { deleteSongFromIndexedDB } from "../../../utils/dbUtils";
import useUserStore from "../../../store/UserState";
import useSongStore from "../../view/spotipitan/store/SongState";
import ConfirmationModal from "../confirmation-modal/confirmation-modal";
import notFoundImage from "../../../images/not-found.png";
import "./song-edit-modal.scss";

const DEFAULT_SONG_POSTER = "https://torresowo.myftp.org:8082/files/cd812ccb405099877fe1964a4ba6cf2d";

export interface SongEditModalProps {
    show: boolean;
    song?: Song | null;
    onClose: () => void;
    onSave?: (savedSong: Song) => void;
    onDelete?: (songId: string | number) => void;
}

const SongEditModal: React.FC<SongEditModalProps> = ({
    show,
    song,
    onClose,
    onSave,
    onDelete
}) => {
    const isCreateMode = !song || !song.id || song.id === -1;
    const { spotip17anUser } = useUserStore(state => state.state);
    const { updateSongInStore, removeSongFromStore } = useSongStore(state => state);

    const [name, setName] = useState("");
    const [authorName, setAuthorName] = useState("");
    const [releaseDate, setReleaseDate] = useState("");
    const [coverUrl, setCoverUrl] = useState("");
    const [coverFile, setCoverFile] = useState<File | null>(null);
    const [coverPreview, setCoverPreview] = useState("");

    const [audioFile, setAudioFile] = useState<File | null>(null);
    const [audioPreviewUrl, setAudioPreviewUrl] = useState<string>("");

    const [canvasFile, setCanvasFile] = useState<File | null>(null);
    const [canvasPreviewUrl, setCanvasPreviewUrl] = useState<string>("");

    const [saving, setSaving] = useState(false);
    const [savingStatus, setSavingStatus] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const coverInputRef = useRef<HTMLInputElement>(null);
    const audioInputRef = useRef<HTMLInputElement>(null);
    const canvasInputRef = useRef<HTMLInputElement>(null);

    // Populate or reset form fields
    useEffect(() => {
        if (!show) {
            setCoverFile(null);
            setCoverPreview("");
            setAudioFile(null);
            setAudioPreviewUrl("");
            setCanvasFile(null);
            setCanvasPreviewUrl("");
            setSavingStatus("");
            return;
        }

        if (isCreateMode) {
            setName("");
            setAuthorName(spotip17anUser?.username || "");
            setReleaseDate("");
            setCoverUrl(DEFAULT_SONG_POSTER);
            setCoverPreview(DEFAULT_SONG_POSTER);
            setCoverFile(null);
            setAudioFile(null);
            setAudioPreviewUrl("");
            setCanvasFile(null);
            setCanvasPreviewUrl("");
            setSavingStatus("");
            return;
        }

        if (song) {
            setName(song.name || "");
            setAuthorName(song.author_name || song.user?.username || "");

            if (song.releaseDate) {
                const dateStr = typeof song.releaseDate === "string"
                    ? song.releaseDate.substring(0, 10)
                    : new Date(song.releaseDate).toISOString().substring(0, 10);
                setReleaseDate(dateStr);
            } else {
                setReleaseDate("");
            }

            const normalizedCover = normalizeFileUrl(song.poster_url || "");
            setCoverUrl(normalizedCover);
            setCoverPreview(normalizedCover);

            setAudioPreviewUrl(song.song_url ? normalizeFileUrl(song.song_url) : "");
            if (song.canvas_url) {
                setCanvasPreviewUrl(normalizeFileUrl(song.canvas_url));
            } else {
                getCanvasVideoUrl(song).then((res) => {
                    if (res?.canvasUrl) {
                        setCanvasPreviewUrl(normalizeFileUrl(res.canvasUrl));
                    }
                }).catch(() => {});
            }
        }
    }, [show, song, isCreateMode, spotip17anUser?.username]);

    const handleSelectCover = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setCoverFile(file);
            setCoverPreview(URL.createObjectURL(file));
        }
    };

    const handleRemoveCover = () => {
        setCoverFile(null);
        if (coverInputRef.current) coverInputRef.current.value = "";
        if (isCreateMode) {
            setCoverPreview(DEFAULT_SONG_POSTER);
            setCoverUrl(DEFAULT_SONG_POSTER);
        } else {
            const original = normalizeFileUrl(song?.poster_url || "");
            setCoverPreview(original);
            setCoverUrl(original);
        }
    };

    const handleSelectAudio = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setAudioFile(file);
            setAudioPreviewUrl(URL.createObjectURL(file));
            // Autocomplete name if empty
            if (!name.trim()) {
                const baseName = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
                setName(baseName);
            }
        }
    };

    const handleRemoveAudio = () => {
        setAudioFile(null);
        if (audioInputRef.current) audioInputRef.current.value = "";
        if (isCreateMode) {
            setAudioPreviewUrl("");
        } else {
            setAudioPreviewUrl(song?.song_url ? normalizeFileUrl(song.song_url) : "");
        }
    };

    const handleSelectCanvas = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.includes("mp4") && !file.name.toLowerCase().endsWith(".mp4")) {
                toast.error("El video Canvas debe ser un archivo en formato MP4.");
                return;
            }
            if (file.size > 30 * 1024 * 1024) {
                toast.error("El video Canvas no puede superar los 30MB.");
                return;
            }
            setCanvasFile(file);
            setCanvasPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleRemoveCanvas = () => {
        setCanvasFile(null);
        if (canvasInputRef.current) canvasInputRef.current.value = "";
        if (isCreateMode) {
            setCanvasPreviewUrl("");
        } else {
            setCanvasPreviewUrl(song?.canvas_url ? normalizeFileUrl(song.canvas_url) : "");
        }
    };

    const uploadCoverIfNeeded = async (): Promise<string | undefined> => {
        if (!coverFile || !spotip17anUser?.token) return undefined;
        setSavingStatus("Subiendo carátula...");
        const folderRes = await CustomApi.post(
            API_POST_CREATE_OR_GET_FOLDER,
            { folderName: "spotip17an songs🎵" },
            { token: spotip17anUser.token }
        );

        return await new Promise<string>((resolve, reject) => {
            CustomApi.uploadFile(
                coverFile,
                coverFile.name,
                spotip17anUser.token,
                folderRes.id,
                (event) => {
                    try {
                        const res = JSON.parse((event.target as any).response);
                        resolve(res.file.url);
                    } catch (err) {
                        reject(err);
                    }
                },
                (err) => reject(err)
            );
        });
    };

    const uploadAudioIfNeeded = async (): Promise<string | undefined> => {
        if (!audioFile || !spotip17anUser?.token) return undefined;
        setSavingStatus("Subiendo archivo de audio...");
        const folderRes = await CustomApi.post(
            API_POST_CREATE_OR_GET_FOLDER,
            { folderName: "spotip17an songs🎵" },
            { token: spotip17anUser.token }
        );

        return await new Promise<string>((resolve, reject) => {
            CustomApi.uploadFile(
                audioFile,
                name.trim() || audioFile.name,
                spotip17anUser.token,
                folderRes.id,
                (event) => {
                    try {
                        const res = JSON.parse((event.target as any).response);
                        resolve(res.file.url);
                    } catch (err) {
                        reject(err);
                    }
                },
                (err) => reject(err)
            );
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim()) {
            toast.error("El nombre de la canción es obligatorio");
            return;
        }

        // Mode: CREATE SONG
        if (isCreateMode) {
            if (!audioFile) {
                toast.error("Debes seleccionar un archivo de audio para la canción.");
                return;
            }
            if (!spotip17anUser?.token) {
                toast.error("No se ha encontrado sesión activa de artista.");
                return;
            }

            setSaving(true);
            try {
                // 1. Subir carátula si se eligió
                let finalPosterUrl = DEFAULT_SONG_POSTER;
                if (coverFile) {
                    const uploadedCover = await uploadCoverIfNeeded();
                    if (uploadedCover) {
                        finalPosterUrl = uploadedCover;
                    }
                }

                // 2. Subir audio
                const uploadedAudioUrl = await uploadAudioIfNeeded();
                if (!uploadedAudioUrl) {
                    throw new Error("No se pudo subir el archivo de audio.");
                }

                // 3. Crear canción en API
                setSavingStatus("Registrando canción...");
                const songBody = {
                    url: uploadedAudioUrl,
                    posterUrl: finalPosterUrl,
                    title: name.trim(),
                    token: spotip17anUser.token,
                    releaseDate: releaseDate ? new Date(releaseDate) : null
                };

                const songRes = await CustomApi.post(API_POST_SONG, undefined, undefined, songBody);
                const newSongId = songRes?.id ?? songRes?.song?.id ?? songRes?.insertId ?? songRes?.songId;

                const createdSong: Song = {
                    id: newSongId ?? Date.now(),
                    name: name.trim(),
                    author_name: authorName.trim() || spotip17anUser.username,
                    song_url: uploadedAudioUrl,
                    poster_url: finalPosterUrl,
                    duration: 0,
                    releaseDate: releaseDate ? new Date(releaseDate) : undefined,
                    user: {
                        id: spotip17anUser.id,
                        username: spotip17anUser.username
                    }
                } as Song;

                // 4. Subir Canvas MP4 si se seleccionó uno
                if (canvasFile && newSongId) {
                    setSavingStatus("Subiendo video Canvas vertical...");
                    try {
                        const canvasRes = await uploadSongCanvas(String(newSongId), canvasFile);
                        if (canvasRes?.canvasUrl) {
                            createdSong.canvas_url = normalizeFileUrl(canvasRes.canvasUrl);
                            clearSongBlobsCache(newSongId);
                        }
                    } catch (cErr: any) {
                        console.error("Error al subir Canvas:", cErr);
                        toast.error("Canción publicada, pero ocurrió un error al subir el Canvas.");
                    }
                }

                toast.success("Canción publicada correctamente");
                if (onSave) {
                    onSave(createdSong);
                }
                onClose();
            } catch (error: any) {
                console.error("Error creating song:", error);
                toast.error(error?.message || "Error al subir la canción");
            } finally {
                setSaving(false);
                setSavingStatus("");
            }
            return;
        }

        // Mode: EDIT SONG
        if (!song) return;

        setSaving(true);
        try {
            // 1. Upload cover if new file selected
            let newPosterUrl: string | undefined = undefined;
            if (coverFile) {
                newPosterUrl = await uploadCoverIfNeeded();
            }

            // 2. Upload new audio file if selected
            let newAudioUrl: string | undefined = undefined;
            if (audioFile) {
                newAudioUrl = await uploadAudioIfNeeded();
            }

            setSavingStatus("Guardando información...");

            // 3. Call updateSong
            const updatedSong = await updateSong({
                songId: song.id,
                name: name.trim(),
                author_name: authorName.trim() || undefined,
                releaseDate: releaseDate ? new Date(releaseDate).toISOString() : null,
                poster_url: newPosterUrl,
                song_url: newAudioUrl,
                file_url: newAudioUrl,
            });

            // 4. Upload Canvas video if selected
            if (canvasFile) {
                setSavingStatus("Subiendo Canvas vertical...");
                try {
                    const canvasRes = await uploadSongCanvas(song.id, canvasFile);
                    if (canvasRes.canvasUrl) {
                        const normalizedCanvas = normalizeFileUrl(canvasRes.canvasUrl);
                        updatedSong.canvas_url = normalizedCanvas;
                        setCanvasPreviewUrl(normalizedCanvas);
                    }
                } catch (cErr: any) {
                    console.error("Error al subir Canvas:", cErr);
                    toast.error("Canción guardada, pero ocurrió un error al subir el Canvas.");
                }
            }

            // Invalidate local memory and IndexedDB caches
            clearSongBlobsCache(song.id);
            await deleteSongFromIndexedDB(String(song.id));

            // 5. Update global store reactively
            updateSongInStore(updatedSong);

            toast.success("Canción actualizada con éxito");
            if (onSave) {
                onSave(updatedSong);
            }
            onClose();
        } catch (error: any) {
            console.error("Error updating song:", error);
            toast.error(error?.message || "Error al actualizar la canción");
        } finally {
            setSaving(false);
            setSavingStatus("");
        }
    };

    const handleConfirmDelete = async () => {
        if (!song) return;
        setDeleting(true);
        try {
            await deleteSong(song.id);
            clearSongBlobsCache(song.id);
            await deleteSongFromIndexedDB(String(song.id));
            removeSongFromStore(song.id);
            toast.success("Canción eliminada correctamente");
            setShowDeleteConfirm(false);
            if (onDelete) {
                onDelete(song.id);
            }
            onClose();
        } catch (error: any) {
            console.error("Error deleting song:", error);
            toast.error(error?.message || "Error al eliminar la canción");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <Modal
                show={show}
                onHide={saving ? undefined : onClose}
                centered
                size="lg"
                className="custom-modal song-edit-modal"
            >
                <Modal.Header closeButton={!saving}>
                    <Modal.Title>
                        <i className={`fa-solid ${isCreateMode ? "fa-cloud-arrow-up" : "fa-pen-to-square"} me-2`}></i>
                        {isCreateMode ? "Subir nueva canción" : "Editar canción"}
                    </Modal.Title>
                </Modal.Header>

                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        {/* Cover + Main Details */}
                        <div className="d-flex flex-column flex-md-row gap-3 mb-3">
                            <div className="d-flex flex-column align-items-center">
                                <div className="song-edit-modal__cover-preview mb-2">
                                    <img
                                        src={coverPreview || notFoundImage}
                                        alt="Portada canción"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src = notFoundImage;
                                        }}
                                    />
                                </div>
                                <input
                                    type="file"
                                    ref={coverInputRef}
                                    style={{ display: "none" }}
                                    accept="image/*"
                                    onChange={handleSelectCover}
                                />
                                <div className="d-flex flex-column gap-1 w-100">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => coverInputRef.current?.click()}
                                        disabled={saving}
                                    >
                                        <i className="fa-solid fa-image me-1"></i>
                                        {coverFile ? "Cambiar carátula" : "Subir carátula"}
                                    </Button>
                                    {coverFile && (
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={handleRemoveCover}
                                            disabled={saving}
                                        >
                                            <i className="fa-solid fa-xmark me-1"></i>
                                            Quitar selección
                                        </Button>
                                    )}
                                </div>
                            </div>

                            <div className="flex-grow-1">
                                <Form.Group className="mb-2">
                                    <Form.Label>Título de la canción <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Nombre de la canción"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                        disabled={saving}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-2">
                                    <Form.Label>Artista / Colaboradores</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Nombre artístico o colaboradores"
                                        value={authorName}
                                        onChange={(e) => setAuthorName(e.target.value)}
                                        disabled={saving}
                                    />
                                    <div className="song-edit-modal__hint mt-1">
                                        Nombre visible en la plataforma para este tema.
                                    </div>
                                </Form.Group>

                                <Form.Group className="mb-2">
                                    <Form.Label>Fecha de lanzamiento</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={releaseDate}
                                        onChange={(e) => setReleaseDate(e.target.value)}
                                        disabled={saving}
                                    />
                                    <div className="song-edit-modal__hint mt-1">
                                        Si se especifica una fecha futura, se considerará un tema no lanzado (Unreleased).
                                    </div>
                                </Form.Group>
                            </div>
                        </div>

                        {/* Audio File Section */}
                        <div className="song-edit-modal__section-box">
                            <div className="song-edit-modal__section-title">
                                <i className="fa-solid fa-music"></i>
                                Archivo de Audio {isCreateMode && <span className="text-danger">*</span>}
                                {audioFile && (
                                    <Badge bg="success" className="ms-auto">
                                        {isCreateMode ? "Audio listo para subir" : "Nuevo audio seleccionado"}
                                    </Badge>
                                )}
                            </div>

                            <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2">
                                <div>
                                    <div className="fw-medium text-truncate" style={{ maxWidth: "340px" }}>
                                        {audioFile 
                                            ? audioFile.name 
                                            : (isCreateMode ? "Ningún archivo de audio seleccionado" : "Archivo de audio actual conservado")}
                                    </div>
                                    <div className="song-edit-modal__hint">
                                        {isCreateMode 
                                            ? "Selecciona el archivo maestro de audio (MP3, WAV, FLAC, M4A, OGG)." 
                                            : "Sube un archivo en mejor calidad o remasterizado (MP3, WAV, FLAC, M4A, OGG)."}
                                    </div>
                                </div>

                                <div className="d-flex gap-2 align-items-center">
                                    <input
                                        type="file"
                                        ref={audioInputRef}
                                        style={{ display: "none" }}
                                        accept="audio/*,.mp3,.wav,.flac,.m4a,.ogg"
                                        onChange={handleSelectAudio}
                                    />
                                    <Button
                                        variant={audioFile ? "primary" : "outline-primary"}
                                        size="sm"
                                        onClick={() => audioInputRef.current?.click()}
                                        disabled={saving}
                                        className="text-nowrap"
                                    >
                                        <i className="fa-solid fa-cloud-arrow-up me-1"></i>
                                        {audioFile ? "Cambiar audio" : (isCreateMode ? "Seleccionar audio" : "Subir nuevo audio")}
                                    </Button>
                                    {audioFile && isCreateMode && (
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={handleRemoveAudio}
                                            disabled={saving}
                                        >
                                            <i className="fa-solid fa-xmark"></i>
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {audioPreviewUrl && (
                                <audio
                                    controls
                                    src={audioPreviewUrl}
                                    className="song-edit-modal__audio-player"
                                />
                            )}
                        </div>

                        {/* Canvas Video Section */}
                        <div className="song-edit-modal__section-box">
                            <div className="song-edit-modal__section-title">
                                <i className="fa-solid fa-film"></i>
                                Video Canvas (Vertical 9:16)
                                {canvasFile && (
                                    <Badge bg="info" className="ms-auto">
                                        Nuevo video seleccionado
                                    </Badge>
                                )}
                            </div>

                            <div className="d-flex align-items-center gap-3">
                                {canvasPreviewUrl && (
                                    <div className="song-edit-modal__canvas-preview">
                                        <video
                                            src={canvasPreviewUrl}
                                            autoPlay
                                            loop
                                            muted
                                            playsInline
                                        />
                                    </div>
                                )}

                                <div className="flex-grow-1">
                                    <div className="song-edit-modal__hint mb-2">
                                        Video en bucle continuo que se reproduce de fondo durante la reproducción en el reproductor (formato MP4 vertical 9:16, máx. 30MB).
                                    </div>

                                    <input
                                        type="file"
                                        ref={canvasInputRef}
                                        style={{ display: "none" }}
                                        accept="video/mp4"
                                        onChange={handleSelectCanvas}
                                    />
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => canvasInputRef.current?.click()}
                                            disabled={saving}
                                        >
                                            <i className="fa-solid fa-video me-1"></i>
                                            {canvasFile || canvasPreviewUrl ? "Cambiar video Canvas" : "Subir video Canvas"}
                                        </Button>
                                        {(canvasFile || (isCreateMode && canvasPreviewUrl)) && (
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={handleRemoveCanvas}
                                                disabled={saving}
                                            >
                                                <i className="fa-solid fa-xmark me-1"></i>
                                                Quitar
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Progress status */}
                        {saving && savingStatus && (
                            <div className="text-center text-muted small mt-2">
                                <Spinner animation="border" size="sm" className="me-2" />
                                {savingStatus}
                            </div>
                        )}
                    </Modal.Body>

                    <Modal.Footer>
                        {!isCreateMode ? (
                            <Button
                                variant="outline-danger"
                                className="song-edit-modal__danger-btn"
                                onClick={() => setShowDeleteConfirm(true)}
                                disabled={saving}
                            >
                                <i className="fa-solid fa-trash me-1"></i>
                                Eliminar canción
                            </Button>
                        ) : (
                            <div />
                        )}

                        <div className="d-flex gap-2">
                            <Button
                                variant="secondary"
                                onClick={onClose}
                                disabled={saving}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-1" />
                                        {isCreateMode ? "Publicando..." : "Guardando..."}
                                    </>
                                ) : (
                                    isCreateMode ? (
                                        <>
                                            <i className="fa-solid fa-cloud-arrow-up me-1"></i>
                                            Publicar canción
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-check me-1"></i>
                                            Guardar cambios
                                        </>
                                    )
                                )}
                            </Button>
                        </div>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Confirmation Modal for safe deletion (only edit mode) */}
            {!isCreateMode && (
                <ConfirmationModal
                    show={showDeleteConfirm}
                    title="Eliminar canción"
                    message="¿Seguro que quieres eliminar esta canción? Esta acción es permanente y se eliminará de todos los álbumes, playlists y listas de favoritos de los usuarios."
                    confirmText="Eliminar permanentemente"
                    cancelText="Cancelar"
                    confirmVariant="danger"
                    loading={deleting}
                    onClose={() => setShowDeleteConfirm(false)}
                    onConfirm={handleConfirmDelete}
                />
            )}
        </>
    );
};

export default SongEditModal;
